import type { ReactNode } from 'react';
import { Card, CardHeader, CardTitle, DataTable, type ColumnDef } from '@paige/ui';
import { useFormat, type Formatter } from '@paige/format';
import type { Funnel as FunnelView, FunnelCompany, FunnelStepName } from './funnel-types';
import { campaignWord, funnelStepWord, partnerWord } from './funnel-words';
import { FunnelSteps } from './FunnelSteps';
import { ActivationHistogram } from './ActivationHistogram';
import { CompanyName } from '../TestMark';
import '../admin.css';

export { stepHref, stoppedHref } from './funnel-links';

/** A step a company takes; visitors are not companies yet. */
export type CompanyStep = Exclude<FunnelStepName, 'visited'>;

export interface FunnelBoardProps {
  funnel: FunnelView;
  /** The step whose companies the table shows; every company when absent. */
  selected?: CompanyStep | undefined;
  /** The step whose drop-offs the table shows: got that far and no further. Wins over `selected`. */
  stopped?: CompanyStep | undefined;
  onSelect: (step: CompanyStep | undefined) => void;
  /** The router's link, so every company opens its page and every step its companies. */
  renderLink: (href: string, children: ReactNode) => ReactNode;
}

/** The order the steps happen in. `setupFinished` is no longer a step: an old link to it shows every company. */
const order: FunnelStepName[] = [
  'visited',
  'signedUp',
  'firstJob',
  'firstPages',
  'firstExport',
  'paid',
];

/** A company reached a step when the step it got to is that far along or further. */
export function reachedStep(company: FunnelCompany, step: FunnelStepName): boolean {
  return order.indexOf(company.reached) >= order.indexOf(step);
}

/** The partner it came through, and today's link when that differs; "Partner, unlinked" when a partner sent it and nobody is linked. */
export function partnerCell(c: FunnelCompany): string {
  if (c.unlinked) return partnerWord('unlinked');
  const at = c.acquiredPartner ?? 'None';
  return c.partner && c.partner !== c.acquiredPartner ? `${at} (now ${c.partner})` : at;
}

function columnsFor(
  f: Formatter,
  renderLink: FunnelBoardProps['renderLink'],
): ColumnDef<FunnelCompany, unknown>[] {
  return [
    {
      accessorKey: 'name',
      header: 'Company',
      cell: (c) => (
        <CompanyName
          name={renderLink(`/admin/customers/${c.row.original.tenant}`, c.row.original.name)}
          test={c.row.original.testCompany}
        />
      ),
    },
    {
      id: 'campaign',
      header: 'Came from',
      accessorFn: (r) => campaignWord(r.source, r.campaign),
    },
    {
      id: 'partner',
      header: 'Partner',
      accessorFn: partnerCell,
    },
    {
      accessorKey: 'signedUpAt',
      header: 'Signed up',
      cell: (c) => f.ago(c.row.original.signedUpAt),
    },
    {
      accessorKey: 'reached',
      header: 'Got as far as',
      cell: (c) => funnelStepWord(c.row.original.reached),
    },
  ];
}

function StepPicker({
  funnel,
  selected,
  stopped,
  onSelect,
}: Pick<FunnelBoardProps, 'funnel' | 'selected' | 'stopped' | 'onSelect'>): ReactNode {
  const steps = funnel.steps.flatMap((s) => (s.step === 'visited' ? [] : [s.step as CompanyStep]));
  return (
    <div className="ad-funnel__picker" role="group" aria-label="Companies at a step">
      <button
        type="button"
        className={selected || stopped ? 'ad-chip' : 'ad-chip ad-chip--on'}
        onClick={() => onSelect(undefined)}
      >
        Everyone who signed up
      </button>
      {steps.map((s) => (
        <button
          key={s}
          type="button"
          className={!stopped && selected === s ? 'ad-chip ad-chip--on' : 'ad-chip'}
          onClick={() => onSelect(s)}
        >
          {funnelStepWord(s)}
        </button>
      ))}
    </div>
  );
}

/** The companies the table shows: those that stopped at a step, reached one, or everyone. */
export function companiesAt(
  funnel: FunnelView,
  selected: CompanyStep | undefined,
  stopped: CompanyStep | undefined,
): FunnelCompany[] {
  if (stopped) return funnel.companies.filter((c) => c.reached === stopped);
  return selected ? funnel.companies.filter((c) => reachedStep(c, selected)) : funnel.companies;
}

function tableTitle(selected: CompanyStep | undefined, stopped: CompanyStep | undefined): string {
  if (stopped) return `Companies that stopped at ${funnelStepWord(stopped).toLowerCase()}`;
  return selected
    ? `Companies that reached ${funnelStepWord(selected).toLowerCase()}`
    : 'Companies that signed up';
}

/**
 * The funnel for the period with the one before as outlines, the step-to-step numbers between its
 * rows, the days to activation, then the companies at the chosen step or drop-off.
 */
export function FunnelBoard(props: FunnelBoardProps): ReactNode {
  const { funnel, selected, stopped, onSelect, renderLink } = props;
  const f = useFormat();
  return (
    <div className="ad-funnel">
      <FunnelSteps funnel={funnel} renderLink={renderLink} />
      <ActivationHistogram companies={funnel.companies} />
      <Card>
        <CardHeader>
          <CardTitle>{tableTitle(selected, stopped)}</CardTitle>
        </CardHeader>
        <StepPicker funnel={funnel} selected={selected} stopped={stopped} onSelect={onSelect} />
        <DataTable
          columns={columnsFor(f, renderLink)}
          data={companiesAt(funnel, selected, stopped)}
          dense
          caption="Companies at the chosen step"
          getRowId={(r) => r.tenant}
          empty="No company got this far in the period."
        />
      </Card>
    </div>
  );
}
